import { useEffect, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { SECONDS } from '@sequencehq/utils/dates'
import { isOAuthAuthorizedReturn } from 'Integrations/hooks/oauthRedirect.constants'
import { useIntegrationsDomainContext } from 'Integrations/communication'
import { ConnectionMethod } from 'Integrations/domain/integrations.domain.types'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export const FAST_POLL_INTERVAL = 2 * SECONDS
export const SLOW_POLL_INTERVAL = 10 * SECONDS

/**
 * Reads of the integration - our own polling plus the screen's - that run at
 * the fast rate before we back off.
 */
export const FAST_POLL_READS = 5

export const useOAuthRedirectConnectionPolling = () => {
  /**
   * We type against the services which fully exist on the server, not the
   * integration services type - we won't hit this path for the 'fake' services
   * that only exist on the client, so it's safe to type in this manner.
   */
  const { integrationId } = useParams<{
    integrationId: DashboardApi20240730.GetIntegration.IntegrationService
  }>()
  const { hubspotV2IntegrationEnabled } = useFeatureFlags()
  const [searchParams, setSearchParams] = useSearchParams()
  const ctx = useIntegrationsDomainContext()

  const isOAuthIntegration = useMemo(() => {
    if (!integrationId) {
      return false
    }

    /**
     * HubSpot only uses our own OAuth flow on v2; on v1 it connects via
     * integration.app, so its config declares no connection method.
     */
    if (hubspotV2IntegrationEnabled && integrationId === 'HubSpot') {
      return true
    }

    return (
      ctx.queries.resolvedIntegrations[integrationId]?.communication?.connecting
        ?.method === ConnectionMethod.OAuth
    )
  }, [
    integrationId,
    hubspotV2IntegrationEnabled,
    ctx.queries.resolvedIntegrations
  ])

  const isAuthorizedReturn =
    isOAuthIntegration && isOAuthAuthorizedReturn(searchParams)

  /**
   * The screens reading this connection read it from the integrations list,
   * not from the endpoint we poll, so the list is what we wait on.
   */
  const isConnected = Boolean(
    integrationId &&
      ctx.queries.resolvedIntegrations[integrationId]?.state === 'CONNECTED'
  )

  const integrationQuery = useQuery(
    dashboard20240730Client.getIntegration,
    integrationId,
    {
      enabled: isAuthorizedReturn && Boolean(integrationId) && !isConnected,
      refetchInterval: query => getConnectionPollInterval(query.state)
    }
  )

  const integrationState = integrationQuery.data?.state
  const lastReadAt = integrationQuery.dataUpdatedAt

  /**
   * `reloadIntegrations` swallows a failed read, so retry it on every fresh
   * read until the list catches up rather than stranding the screen.
   */
  useEffect(() => {
    if (
      !isAuthorizedReturn ||
      isConnected ||
      integrationState !== 'CONNECTED'
    ) {
      return
    }

    void ctx.mutators.external.in.reloadIntegrations()
  }, [
    isAuthorizedReturn,
    isConnected,
    integrationState,
    lastReadAt,
    ctx.mutators.external.in
  ])

  useEffect(() => {
    if (!isAuthorizedReturn || !isConnected) {
      return
    }

    setSearchParams({}, { replace: true })
  }, [isAuthorizedReturn, isConnected, setSearchParams])
}

/**
 * A connection usually lands in the first few reads. After those we keep
 * checking slowly, indefinitely, rather than giving up on a slow backend.
 */
export const getConnectionPollInterval = (reads: {
  dataUpdateCount: number
  errorUpdateCount: number
}) => {
  const totalReads = reads.dataUpdateCount + reads.errorUpdateCount

  return totalReads >= FAST_POLL_READS ? SLOW_POLL_INTERVAL : FAST_POLL_INTERVAL
}
